import React, { useState, useEffect, useRef } from 'react';
import { DashboardContext } from './DashboardContext';
import useDashboardState from './useDashboardState';
import usePlanManager from './usePlanManager';
import DashboardLayout from './components/layout/DashboardLayout';
import LoginScreen from './components/LoginScreen';
import PlanBar from './components/PlanBar';
import ForkPrompt from './components/ForkPrompt';
import { checkBackend, getPlan } from './lib/api';

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FCFCFC', fontFamily: "'Outfit', sans-serif" }}>
          <div style={{ textAlign: 'center', maxWidth: 480, padding: 24 }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1A1A1A', marginBottom: 8 }}>Something went wrong</h1>
            <p style={{ fontSize: 14, color: '#737373', marginBottom: 16 }}>{this.state.error?.message || 'An unexpected error occurred.'}</p>
            <button onClick={() => window.location.reload()} style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid #D4D4D4', background: '#F5F5F5', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Reload</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Section components
import PlanVerdictSection from './components/sections/PlanVerdictSection';
import OverviewSection from './components/sections/OverviewSection';
import MarketSettingsSection from './components/sections/MarketSettingsSection';
import StrategyBuilderSection from './components/sections/StrategyBuilderSection';
import BenchmarksSection from './components/sections/BenchmarksSection';
import TaxEconomicsSection from './components/sections/TaxEconomicsSection';
import AnalyticsSection from './components/sections/AnalyticsSection';
import GlossarySection from './components/sections/GlossarySection';
import StrategyReportSection from './components/sections/StrategyReportSection';
import CPAReportSection from './components/sections/CPAReportSection';
import ClientsSection from './components/sections/ClientsSection';
import PlanCompareSection from './components/sections/PlanCompareSection';

const SECTIONS = {
  'plan': PlanVerdictSection,
  'overview': OverviewSection,
  'compare': PlanCompareSection,
  'clients': ClientsSection,
  'market-settings': MarketSettingsSection,
  'strategy-builder': StrategyBuilderSection,
  'benchmarks': BenchmarksSection,
  'tax-economics': TaxEconomicsSection,
  'analytics': AnalyticsSection,
  'strategy-report': StrategyReportSection,
  'cpa-report': CPAReportSection,
  'glossary': GlossarySection,
};

function Dashboard({ user }) {
  const state = useDashboardState();
  const planMgr = usePlanManager({ user, serializePlan: state.serializePlan, loadPlanData: state.loadPlanData });
  const [activeSection, setActiveSection] = useState('plan');

  const isClient = user?.role === 'client';
  const ActiveComponent = SECTIONS[activeSection] || PlanVerdictSection;

  return (
    <DashboardContext.Provider value={{ ...state, planMgr, user }}>
      <DashboardLayout activeSection={activeSection} onSectionChange={setActiveSection} role={isClient ? 'client' : 'advisor'}>
        <ErrorBoundary>
          <PlanBar />
          <ActiveComponent />
          <ForkPrompt />
        </ErrorBoundary>
      </DashboardLayout>
    </DashboardContext.Provider>
  );
}

/* Print view — what headless Chromium loads when the server renders a PDF.
   No sidebar, no plan bar, no buttons: just the report, so the print stylesheet
   governs the page on its own. Reached at /?print=1&planId=N.

   `data-pdf-ready` is the handshake. Without it the renderer races React and
   captures blank charts, since recharts paints after its container measures. */
function PrintView({ user, planId }) {
  const state = useDashboardState();
  const planMgr = usePlanManager({ user, serializePlan: state.serializePlan, loadPlanData: state.loadPlanData });
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const opened = useRef(false);

  useEffect(() => {
    if (opened.current) return;
    opened.current = true;
    getPlan(planId)
      .then(plan => planMgr.openPlan(plan))
      .catch(() => setStatus('error'));
  }, [planId, planMgr]);

  // Wait for the charts to actually exist rather than for a fixed delay.
  // requestAnimationFrame is unreliable here: headless Chromium throttles frames
  // when nothing is compositing, so a frame-based signal fires sometimes and
  // hangs the render the rest of the time.
  const activePlan = planMgr.activePlan;
  useEffect(() => {
    if (!activePlan || status !== 'loading') return;
    let cancelled = false;
    const started = Date.now();
    const poll = () => {
      if (cancelled) return;
      const charts = document.querySelectorAll('.recharts-surface');
      const painted = charts.length > 0 &&
        Array.from(charts).every(c => c.getBoundingClientRect().height > 0);
      // Give up waiting after 15s and render whatever is there — a report with a
      // missing chart beats no report at all.
      if (painted || Date.now() - started > 15000) setStatus('ready');
      else setTimeout(poll, 150);
    };
    const t = setTimeout(poll, 150);
    return () => { cancelled = true; clearTimeout(t); };
  }, [activePlan, status]);

  if (status === 'error') {
    return <div data-pdf-error="true" style={{ padding: 40, fontFamily: "'Outfit', sans-serif" }}>Plan not found.</div>;
  }

  return (
    <DashboardContext.Provider value={{ ...state, planMgr, user }}>
      {/* Mirrors DashboardLayout's #root > div > main > div nesting on purpose.
          The print stylesheet unlocks the viewport-locked layout by targeting
          those exact selectors, so a flatter tree here would print differently
          from what the Print button produces. */}
      <div data-pdf-ready={status === 'ready' ? 'true' : 'false'} className="pdf-render-root">
        <main>
          <div>
            <ErrorBoundary>
              <StrategyReportSection printMode />
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </DashboardContext.Provider>
  );
}

export default function App() {
  const params = new URLSearchParams(window.location.search);
  const printPlanId = params.get('print') === '1' ? Number(params.get('planId')) : null;
  const [authState, setAuthState] = useState('checking');
  const [user, setUser] = useState(null); // { username, role, displayName } | null (local-only)

  useEffect(() => {
    const timeout = setTimeout(() => setAuthState('authenticated'), 5000); // 5s fallback
    checkBackend().then(result => {
      clearTimeout(timeout);
      if (!result.available) {
        setAuthState('authenticated'); // No backend — local-only mode
      } else if (result.authenticated) {
        setUser({ username: result.username, role: result.role || 'advisor', displayName: result.displayName });
        setAuthState('authenticated');
      } else {
        setAuthState('login');
      }
    }).catch(() => {
      clearTimeout(timeout);
      setAuthState('authenticated'); // Network error — local-only fallback
    });
    return () => clearTimeout(timeout);
  }, []);

  if (authState === 'checking') {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#FCFCFC',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Outfit', sans-serif",
      }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#1A1A1A' }}>
            Miner<span style={{ color: '#92700C' }}>Ops</span>
          </h1>
          <p style={{ fontSize: '13px', color: '#737373', marginTop: '8px' }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (authState === 'login') {
    return <LoginScreen onAuthenticated={(data) => {
      setUser({ username: data.username, role: data.role || 'advisor', displayName: data.display_name });
      setAuthState('authenticated');
    }} />;
  }

  if (printPlanId) return <PrintView user={user} planId={printPlanId} />;

  return <Dashboard user={user} />;
}
